import { Component, OnInit, OnDestroy, Output, EventEmitter, Input, HostListener, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { LayoutService } from '../../core/services/layout/layout.service';
import { NavigationService, NavigationItem, NavigationSubItem } from '../../core/services/sidebar/sidebar.service';
import { SubmenuCountService } from '../../core/services/sidebar/submenu-count.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ProjectHoursService } from '@app/core/services/sidebar/project-hours.service';


interface WidgetItem {
  iconClass: string;
  label: string;
  count?: number;
  link?: string;
  type?: 'default' | 'warning' | 'danger';
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, MatTooltipModule],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss'
})
export class Sidebar implements OnInit, OnDestroy, OnChanges {
  @Input() isCollapsed = false;
  @Input() isChangePasswordOpen = false;
  @Output() toggleCollapse = new EventEmitter<void>();
  @Input() userInfoBg: string = 'assets/images/theme-banner.png';
  userName: string | null = null;
  isTransitioning = false;
  greeting = 'Good Morning';
  currentTimePeriod: 'morning' | 'afternoon' | 'evening' | 'night' = 'morning';
  needsTextOverlay = false;
  navigationItems: NavigationItem[] = [];
  private subscription = new Subscription();
  private routerSubscription: Subscription;
  activeHeader: NavigationItem | null = null;
  activeSubItem: NavigationSubItem | null = null;
  isRadialMenuOpen = false;
  isDashboard = false;
  isMainPage = false; // For header navigation highlighting
  openWidgetDropdown: number | null = null;
  openSectionDropdown: string | null = null;

  // Radial menu properties
  radialMenuItems = [
    {
      id: 'timesheets',
      label: 'Timesheets',
      icon: 'wl_icon_timesheet-icon',
      route: '/timesheets'
    },
    {
      id: 'projects-all',
      label: 'Projects',
      icon: 'icon-all_projects',
      route: '/projects/projects-all'
    },
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'icon-projects_i_own',
      route: '/dashboard'
    },
    {
      id: 'requests',
      label: 'Requests',
      icon: 'icon-task',
      route: '/requests'
    },
    {
      id: 'clients',
      label: 'Clients',
      icon: 'wl_icon_clients',
      route: '/clients-list'
    }
  ];

  statusItems = [
    { label: 'Working on', count: 0 },
    { label: 'Pending', count: 0 },
    { label: 'Approvals', count: 0 }
  ];

  widgets: WidgetItem[] = [
    {
      iconClass: 'wl_icon_mytask',
      label: 'My Tasks',
      count: 25
    },
    {
      iconClass: 'wl_icon_myapprovals',
      label: 'My Approvals',
      count: 10
    },
    {
      iconClass: 'wl_icon_pending',
      label: 'Pending',
      count: 18,
      type: 'danger'
    },
    {
      iconClass: 'wl_icon_qaissues',
      label: 'QA Issues',
      count: 2,
      type: 'warning'
    },
    {
      iconClass: 'wl_icon_timesheet-icon',
      label: 'My Timesheet'
    },
    {
      iconClass: 'wl_icon_workingon',
      label: 'Working On',
      count: 18
    },
    {
      iconClass: 'wl_icon_request',
      label: 'Requests',
      count: 18
    },
    {
      iconClass: 'wl_icon_queues',
      label: 'Queues',
      count: 3
    },
    {
      iconClass: 'wl_icon_whosoff',
      label: 'Who\'s Off',
      count: 2,
      type: 'warning'
    }
  ];

  constructor(
    public layoutService: LayoutService,
    private router: Router,
    private navigationService: NavigationService,
    private submenuCountService: SubmenuCountService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private projectHoursService: ProjectHoursService
  ) {
    // Subscribe to navigation config once in constructor
    this.subscription.add(
      this.navigationService.getNavigationConfig().subscribe((data: any) => {
        this.navigationItems = data;
      })
    );

    this.routerSubscription = this.router.events.subscribe((event) => {
      this.isDashboard = this.router.url === '/' || this.router.url === '/dashboard';
      this.isMainPage = this.router.url === '/' || this.router.url === '/dashboard' || this.router.url === '/projects' || this.router.url.startsWith('/projects/');
    });
  }

  toggleSidebar() {
    // Add transitioning class to prevent overflow issues during animation
    this.isTransitioning = true;
    
    this.layoutService.toggleSidebar();
    
    // Remove transitioning class after animation completes (250ms)
    setTimeout(() => {
      this.isTransitioning = false;
      this.cdr.detectChanges();
    }, 250);
  }

  ngOnInit(): void {
    this.userName = localStorage.getItem('userName') || 'User';
    this.setGreetingAndBg();
    this.getWorkingCount();

    // Subscribe to navigation changes
    this.subscription.add(
      this.navigationService.getActiveHeader().subscribe(header => {
        // console.log('=== SIDEBAR - Active Header Changed ===');
        // console.log('New active header:', header);
        this.activeHeader = header;
        // Auto-open submenu if header is active and has sub-items
        if (header && this.hasChildren(header)) {
          // console.log('Auto-opening submenu for:', header.to);
          this.openSubmenus.add(header.to);
        }
      })
    );

    this.subscription.add(
      this.navigationService.getActiveSubItem().subscribe(subItem => {
        this.activeSubItem = subItem;
        // Auto-open parent submenu when sub-item is active
        if (subItem) {
          const parentHeader = this.navigationItems.find(item =>
            item.subs && item.subs.some(sub => sub.to === subItem.to)
          );
          if (parentHeader) {
            this.openSubmenus.add(parentHeader.to);
          }
        }
      })
    );

    // Subscribe to route changes to auto-open current page submenu and update menu states
    this.subscription.add(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          const currentSubmenu = this.getCurrentPageSubmenu();
          if (currentSubmenu && this.hasChildren(currentSubmenu)) {
            this.openSubmenus.add(currentSubmenu.to);
          }
          
          // Debug logging for detail pages
          const detailPageInfo = this.getCurrentDetailPageInfo();
          if (detailPageInfo) {
            console.log('🔍 Sidebar: Detected detail page:', detailPageInfo);
            console.log('🔍 Sidebar: Current submenu:', currentSubmenu);
            console.log('🔍 Sidebar: Navigation items:', this.navigationItems);
          }
          
          // Force change detection to update menu states
          setTimeout(() => {
            this.cdr.detectChanges();
            // Force refresh menu state after route change
            this.forceRefreshMenuState();
          }, 0);
        }
      })
    );

    // Initial check for dashboard
    this.isDashboard = this.router.url === '/' || this.router.url === '/dashboard';
    this.isMainPage = this.router.url === '/' || this.router.url === '/dashboard' || this.router.url === '/projects' || this.router.url.startsWith('/projects/');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userInfoBg'] && !changes['userInfoBg'].firstChange) {
      // When userInfoBg input changes, trigger change detection
      this.cdr.detectChanges();

      // If theme was disabled, refresh to show correct time-based background
      if (!this.isThemeActive()) {
        this.setGreetingAndBg();
        this.cdr.detectChanges();
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.routerSubscription.unsubscribe();
  }

  /**
   * Set greeting and userInfoBg based on time of day, using random background from assets/images/left_sidebar/greeting_bg
   * Only sets background if no theme is active from layout
   */
  private setGreetingAndBg() {
    const hour = new Date().getHours();
    let period = 'morning';
    if (hour >= 5 && hour < 12) {
      this.greeting = 'Good Morning';
      period = 'morning';
      this.needsTextOverlay = true; // Bright daylight hours need overlay
    } else if (hour >= 12 && hour < 17) {
      this.greeting = 'Good Afternoon';
      period = 'afternoon';
      this.needsTextOverlay = true; // Bright daylight hours need overlay
    } else if (hour >= 17 && hour < 20) {
      this.greeting = 'Good Evening';
      period = 'evening';
      this.needsTextOverlay = false; // Dusk hours, less bright
    } else {
      this.greeting = 'Good Evening';
      period = 'night';
      this.needsTextOverlay = false; // Dark hours, no overlay needed
    }
    this.currentTimePeriod = period as 'morning' | 'afternoon' | 'evening' | 'night';

    // Only set background if no theme is active OR if userInfoBg is from the layout's time-based system
    if (!this.isThemeActive() || this.isLayoutTimeBasedBackground()) {
      // List of available images for each period
      const bgImages: { [key: string]: string[] } = {
        morning: [
          'assets/images/left_sidebar/greeting_bg/greet_bg_morning_1.jpg',
          'assets/images/left_sidebar/greeting_bg/greet_bg_morning_2.jpg',
        ],
        afternoon: [
          'assets/images/left_sidebar/greeting_bg/greet_bg_afternoon_1.jpg',
          'assets/images/left_sidebar/greeting_bg/greet_bg_afternoon_2.png',
        ],
        evening: [
          'assets/images/left_sidebar/greeting_bg/greet_bg_evening_1.png',
          'assets/images/left_sidebar/greeting_bg/greet_bg_evening_2.png',
        ],
        night: [
          'assets/images/left_sidebar/greeting_bg/greet_bg_night_1.jpg',
          'assets/images/left_sidebar/greeting_bg/greet_bg_night_2.jpg',
        ],
      };
      const imgs = bgImages[period] || bgImages['morning'];
      this.userInfoBg = imgs[Math.floor(Math.random() * imgs.length)];
    }
  }

  private isThemeActive(): boolean {
    return localStorage.getItem('theme-background-active') === 'true';
  }

  private isLayoutTimeBasedBackground(): boolean {
    // Check if the current userInfoBg is from layout's time-based system
    const layoutTimeBasedBgs = [
      'assets/images/theme-banner.png',
      'assets/images/theme-banner-ocean.png',
      'assets/images/theme-banner-dark.png'
    ];
    return layoutTimeBasedBgs.includes(this.userInfoBg);
  }

  onAddClick() {
    // Toggle radial menu
    this.isRadialMenuOpen = !this.isRadialMenuOpen;
  }

  onRadialMenuItemClick(item: any, event: Event) {
    event.stopPropagation();
    this.isRadialMenuOpen = false;

    if (item.queryParams) {
      this.router.navigate([item.route], { queryParams: item.queryParams });
    } else {
      this.router.navigate([item.route]);
    }
  }

  public toggleWidgetDropdown(index: number, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    
    if (this.openWidgetDropdown === index) {
      this.openWidgetDropdown = null;
    } else {
      this.openWidgetDropdown = index;
    }
  }

  public onWidgetDropdownClick(action: string, widget: WidgetItem, index: number): void {
    console.log(`Widget dropdown action: ${action}`, widget);
    
    // Close the dropdown
    this.openWidgetDropdown = null;
    
    // Handle different actions
    switch (action) {
      case 'edit':
        // Add edit functionality here
        console.log('Edit widget:', widget);
        break;
      case 'settings':
        // Add settings functionality here
        console.log('Settings for widget:', widget);
        break;
      case 'remove':
        // Add remove functionality here
        console.log('Remove widget:', widget);
        break;
      default:
        console.log('Unknown action:', action);
    }
  }

  public toggleSectionDropdown(sectionId: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    
    if (this.openSectionDropdown === sectionId) {
      this.openSectionDropdown = null;
    } else {
      this.openSectionDropdown = sectionId;
    }
  }

  public onSectionDropdownClick(action: string, sectionId: string): void {
    console.log(`Section dropdown action: ${action} for section: ${sectionId}`);
    
    // Close the dropdown
    this.openSectionDropdown = null;
    
    // Handle different actions
    switch (action) {
      case 'edit':
        // Add edit functionality here
        console.log('Edit section:', sectionId);
        break;
      case 'settings':
        // Add settings functionality here
        console.log('Settings for section:', sectionId);
        break;
      case 'hide':
        // Add hide functionality here
        console.log('Hide section:', sectionId);
        break;
      default:
        console.log('Unknown action:', action);
    }
  }

  // Close radial menu when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (this.isRadialMenuOpen) {
      const target = event.target as HTMLElement;
      if (!target.closest('.radial_menu_btn') && !target.closest('.radial_menu')) {
        this.isRadialMenuOpen = false;
      }
    }
    
    // Close widget dropdown when clicking outside
    if (this.openWidgetDropdown !== null) {
      const target = event.target as HTMLElement;
      if (!target.closest('.widget_dropdown_container')) {
        this.openWidgetDropdown = null;
      }
    }
    
    // Close section dropdown when clicking outside
    if (this.openSectionDropdown !== null) {
      const target = event.target as HTMLElement;
      if (!target.closest('.section_dropdown_container')) {
        this.openSectionDropdown = null;
      }
    }
  }

  // Keyboard navigation support
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (this.isRadialMenuOpen) {
      switch (event.key) {
        case 'Escape':
          this.isRadialMenuOpen = false;
          break;
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
          const index = parseInt(event.key) - 1;
          if (this.radialMenuItems[index]) {
            this.onRadialMenuItemClick(this.radialMenuItems[index], event);
          }
          break;
      }
    }
  }

  hasChildren(item: NavigationItem): boolean {
    return !!item.subs && item.subs.length > 0;
  }

  onSubItemClick(subItem: NavigationSubItem): void {
    // console.log('=== SIDEBAR - Sub Item Click ===');
    // console.log('Clicked sub item:', subItem);

    this.navigationService.setActiveSubItem(subItem);

    // Special handling for "Project Documents" - now functional
    if (subItem.objCode === 'PROJECT' && subItem.label === 'Documents') {
      // Navigate to Documents page
      const currentUrl = this.router.url;
      const projectMatch = currentUrl.match(/\/projects\/project-details\/([^\/]+)/);

      if (projectMatch) {
        const projectId = projectMatch[1];
        this.router.navigate(['/projects/project-details', projectId, 'documents']);
      } else {
        // Fallback for safety
        this.router.navigate([subItem.to]);
      }
      return;
    }

    // Special handling for "Projects I Own" menu item
    if (subItem.to === '/projects/projects-i-own') {
      // Override navigation to redirect to projects-all with scope parameter
      this.router.navigate(['/projects/projects-all'], {
        queryParams: { scope: 'my-project' }
      });
      return;
    }

    // Special handling for "All Projects" menu item
    if (subItem.to === '/projects/projects-all') {
      // Navigate to clean projects-all URL without any query parameters
      // This will trigger the query parameter handler to clear scope filters
      this.router.navigate(['/projects/projects-all']);
      console.log('🔍 All Projects menu clicked - navigating to clean URL');
      return;
    }

    if (subItem.objCode === 'PROJECT') {
      // Get current project ID from route
      const currentUrl = this.router.url;
      const projectMatch = currentUrl.match(/\/projects\/project-details\/([^\/]+)/);

      if (projectMatch) {
        const projectId = projectMatch[1];
        const subRoutePath = subItem.to.split('/').pop();

        if (subRoutePath) {
          this.router.navigate(['/projects/project-details', projectId, subRoutePath]);
        } else {
          // Fallback for safety
          this.router.navigate([subItem.to]);
        }
      } else {
        // If no project ID found, use default navigation
        this.router.navigate([subItem.to]);
      }
    } else {
      // Check for specific detail page types first
      const currentUrl = this.router.url;
      const detailPageTypes = ['task', 'revision', 'issue', 'request'];
      let detailNavigation = null;
      
      for (const detailType of detailPageTypes) {
        if (currentUrl.includes(`/${detailType}/${detailType}-details/`)) {
          // Extract the ID from the current URL
          const detailMatch = currentUrl.match(new RegExp(`\\/${detailType}\\/${detailType}-details\\/([^\\/]+)`));
          if (detailMatch) {
            const detailId = detailMatch[1];
            const subRoutePath = subItem.to.split('/').pop();
            
            // Check if the sub-item belongs to this detail page type
            if (subItem.to.startsWith(`/${detailType}/`)) {
              if (subRoutePath) {
                detailNavigation = [`/${detailType}/${detailType}-details`, detailId, subRoutePath];
              } else {
                detailNavigation = [`/${detailType}/${detailType}-details`, detailId];
              }
              break;
            }
          }
        }
      }
      
      // If no specific detail page match found, try dynamic navigation
      if (!detailNavigation) {
        detailNavigation = this.handleDetailPageNavigation(subItem);
      }
      
      if (detailNavigation) {
        this.router.navigate(detailNavigation);
      } else {
        // If no detail page match found, use default navigation
        this.router.navigate([subItem.to]);
      }
    }
  }

  isSubItemActive(subItem: NavigationSubItem): boolean {
    const currentUrl = this.router.url;
    const fullUrl = window.location.href; // Get full URL including query params

    // Special handling for "Projects I Own" menu item - OVERRIDE navigation service logic
    if (subItem.to === '/projects/projects-i-own' || 
        subItem.label?.toLowerCase().includes('projects i own') ||
        subItem.label?.toLowerCase().includes('my projects') ||
        subItem.label?.toLowerCase().includes('i own')) {
      // Check if we're on projects-all with scope=my-project
      if (currentUrl.includes('/projects/projects-all')) {
        // Handle hash routing - query params come after the hash
        const urlParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
        const scope = urlParams.get('scope');
        const isActive = scope === 'my-project';
        return isActive;
      }
      return false;
    }

    // Special handling for "All Projects" menu item - OVERRIDE navigation service logic
    if (subItem.to === '/projects/projects-all') {
      // Check if we're on projects-all without scope parameter
      if (currentUrl.includes('/projects/projects-all')) {
        // Handle hash routing - query params come after the hash
        const urlParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
        const scope = urlParams.get('scope');
        const isActive = !scope || scope !== 'my-project';
        return isActive;
      }
      return false;
    }

    // For all other items, use the navigation service logic
    const isActive = this.navigationService.isSubItemActive(subItem);

    // Additional check for current route
    if (!isActive) {
      // Check if current URL matches the sub-item route
      if (currentUrl === subItem.to) {
        return true;
      }

      // Check for project/task detail routes
      if (subItem.objCode === 'PROJECT' && currentUrl.includes('/projects/project-details/')) {
        const subRoutePath = subItem.to.split('/').pop();
        // Remove hash fragment if present for comparison
        const urlWithoutHash = currentUrl.split('#')[0];
        const currentSubRoute = urlWithoutHash.split('/').pop();
        return subRoutePath === currentSubRoute;
      }

      // Check for specific detail page types
      const detailPageTypes = ['task', 'revision', 'issue', 'request'];
      for (const detailType of detailPageTypes) {
        if (currentUrl.includes(`/${detailType}/${detailType}-details/`)) {
          // Check if the sub-item belongs to this detail page type
          if (subItem.to.startsWith(`/${detailType}/`)) {
            const subRoutePath = subItem.to.split('/').pop();
            // Remove hash fragment if present for comparison
            const urlWithoutHash = currentUrl.split('#')[0];
            const currentSubRoute = urlWithoutHash.split('/').pop();
            
            // Handle special cases like 'edit'
            if (subRoutePath === 'edit' && currentSubRoute === 'edit') {
              return true;
            }
            
            // Handle detail page main route
            if (subRoutePath === undefined && !currentSubRoute) {
              return true;
            }
            
            return subRoutePath === currentSubRoute;
          }
        }
      }

      // Handle detail pages dynamically (fallback)
      const detailPageMatch = this.handleDetailPageActiveCheck(subItem, currentUrl);
      if (detailPageMatch !== null) {
        return detailPageMatch;
      }
    }

    return isActive;
  }

  isHeaderActive(header: NavigationItem): boolean {
    const isActive = this.navigationService.isHeaderActive(header);

    // Additional check for dashboard routes
    if (header.to === '/dashboard' || header.to === '/') {
      return this.isMainPage;
    }

    return isActive;
  }

  shouldShowSidebar(): boolean {
    // Show sidebar if we have an active header with subs, or if we have navigation items
    const hasActiveHeader = this.activeHeader !== null && !this.activeHeader.hideSidebar;
    const hasNavigationItems = this.navigationItems && this.navigationItems.length > 0;
    
    // Also show sidebar if we're on a detail page (even without active header)
    const isOnDetailPage = this.getCurrentDetailPageInfo() !== null;
    
   
    
    return hasActiveHeader || hasNavigationItems || isOnDetailPage;
  }

  private openSubmenus = new Set<string>();

  toggleSubmenu(navItem: NavigationItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.openSubmenus.has(navItem.to)) {
      this.openSubmenus.delete(navItem.to);
    } else {
      this.openSubmenus.add(navItem.to);
    }
  }

  isSubmenuOpen(navItem: NavigationItem): boolean {
    return this.openSubmenus.has(navItem.to);
  }

  getCurrentPageSubmenu(): NavigationItem | null {
    // Use the NavigationService's active header which is already set based on current route
    if (this.activeHeader) {
      return this.activeHeader;
    }
    
    // If no active header, check if we're on a detail page and try to find the appropriate navigation item
    const detailPageInfo = this.getCurrentDetailPageInfo();
    if (detailPageInfo) {
      // Look for navigation items that match the detail page type
      for (const item of this.navigationItems) {
        if (item.subs && item.subs.some(sub => sub.to.startsWith(`/${detailPageInfo.type}/`))) {
          return item;
        }
      }
    }
    
    return null;
  }

  getSubmenuCount(subItem: NavigationSubItem): number {
    return this.submenuCountService.getCountFor(subItem);
  }

  getSubmenuHours(subItem: NavigationSubItem): string {
    return this.submenuCountService.getHoursFor(subItem);
  }

  getSubmenuIcon(subItem: NavigationSubItem): string {
    // Map submenu items to existing SVG icons
    const iconMap: { [key: string]: string } = {
      // Project related icons
      'all-projects': 'wl_icon_all_project',
      'projects-i-own': 'wl_icon_project_i_own',
      'project-details': 'wl_icon_prtdetail',
      'add-project': 'wl_icon_approvals',
      'edit-project': 'wl_icon_revisions',

      // Client related icons
      'clients': 'assets/images/people.svg',
      'client-details': 'assets/images/person.png',
      'add-client': 'assets/images/apply.svg',

      // Dashboard related icons
      'dashboard': 'assets/images/dt1.svg',
      'overview': 'assets/images/dt2.svg',
      'analytics': 'assets/images/dt3.svg',
      'reports': 'assets/images/dt4.svg',

      // Task related icons
      'tasks': 'wl_icon_task',
      'my-tasks': 'wl_icon_mytask',
      'task-details': 'wl_icon_task_details',
      'add-task': 'wl_icon_approvals',

      // Team related icons
      'team': 'assets/images/left_sidebar/my_team.svg',
      'my-team': 'assets/images/left_sidebar/my_team.svg',
      'team-members': 'assets/images/people.svg',

      // Approval related icons
      'approvals': 'wl_icon_approvals',
      'my-approvals': 'assets/images/left_sidebar/my_approvals.svg',
      'pending-approvals': 'assets/images/left_sidebar/pending.svg',

      // Time related icons
      'timesheet': 'assets/images/left_sidebar/my_timesheet.svg',
      'my-timesheet': 'assets/images/left_sidebar/my_timesheet.svg',
      'time-log': 'assets/images/calender.svg',
      'hours': 'wl_icon_log-time',

      // Issue related icons
      'issues': 'wl_icon_issues',
      'documents': 'wl_icon_documents',
      'updates': 'wl_icon_updates',
      'qa-issues': '',
      'bugs': '',

      // Queue related icons
      'queues': 'assets/images/left_sidebar/queues.svg',
      'my-queues': 'assets/images/left_sidebar/queues.svg',

      // Request related icons
      'requests': 'wl_icon_request',
      'my-requests': 'wl_icon_request',
      'revisions': 'wl_icon_revisions',

      // Leave related icons
      'leave': 'assets/images/left_sidebar/apply_leave.svg',
      'apply-leave': 'assets/images/left_sidebar/apply_leave.svg',
      'whos-off': 'assets/images/left_sidebar/whos_off.svg',

      // Assignment related icons
      'assignments': 'assets/images/left_sidebar/daily_assignments.svg',
      'daily-assignments': 'assets/images/left_sidebar/daily_assignments.svg',

      // Settings related icons
      'settings': 'assets/images/settings.svg',
      'profile': 'assets/images/user.png',
      'account': 'assets/images/user.png',

      // Default icons
      'default': 'wl_icon_template',
      'list': 'assets/images/left_sidebar/my_tasks.svg',
      'view': 'assets/images/left_sidebar/my_approvals.svg',
      'create': 'assets/images/apply.svg',
      'edit': 'assets/images/edit.svg',
      'delete': 'assets/images/close.svg'
    };

     // Try to match by label (case insensitive) - add null check
     if (subItem.label) {
      const label = subItem.label.toLowerCase().replace(/\s+/g, '-');
      if (iconMap[label]) {
        return iconMap[label];
      }
    }

    // Try to match by route path
    if (subItem.to) {
      const route = subItem.to.toLowerCase().replace(/\//g, '-').replace(/^-/, '');
      if (iconMap[route]) {
        return iconMap[route];
      }
    }

    if (subItem.objCode) {
      if (iconMap[subItem.objCode.toLowerCase()]) {
        return iconMap[subItem.objCode.toLowerCase()];
      }
    }

    // Return default icon if no match found
    return iconMap['default'] || 'wl_icon_template';
  }

  private forceRefreshMenuState(): void {
    // Force refresh the menu state by triggering change detection
    this.cdr.detectChanges();
    
    // Also force the navigation service to update
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 100);
  }

  setUserInfoBg(bg: string) {
    this.userInfoBg = bg;
    this.cdr.detectChanges();
  }

  refreshUserInfoBackground() {
    // Force refresh the user info background
    this.setGreetingAndBg();
    this.cdr.detectChanges();
  }

  forceRefreshTimeBasedBackground() {
    // Force refresh to current time-based background (ignoring theme state temporarily)
    const hour = new Date().getHours();
    let period = 'morning';

    if (hour >= 5 && hour < 12) {
      this.greeting = 'Good Morning';
      period = 'morning';
      this.needsTextOverlay = true;
    } else if (hour >= 12 && hour < 17) {
      this.greeting = 'Good Afternoon';
      period = 'afternoon';
      this.needsTextOverlay = true;
    } else if (hour >= 17 && hour < 20) {
      this.greeting = 'Good Evening';
      period = 'evening';
      this.needsTextOverlay = false;
    } else {
      this.greeting = 'Good Night';
      period = 'night';
      this.needsTextOverlay = false;
    }

    this.currentTimePeriod = period as 'morning' | 'afternoon' | 'evening' | 'night';

    // Always set time-based background when this method is called
    const bgImages: { [key: string]: string[] } = {
      morning: [
        'assets/images/left_sidebar/greeting_bg/greet_bg_morning_1.jpg',
        'assets/images/left_sidebar/greeting_bg/greet_bg_morning_2.jpg',
      ],
      afternoon: [
        'assets/images/left_sidebar/greeting_bg/greet_bg_afternoon_1.jpg',
      ],
      evening: [
        'assets/images/left_sidebar/greeting_bg/greet_bg_evening_1.png',
      ],
      night: [
        'assets/images/left_sidebar/greeting_bg/greet_bg_night_1.jpg',
        'assets/images/left_sidebar/greeting_bg/greet_bg_night_2.jpg',
      ],
    };

    const imgs = bgImages[period] || bgImages['morning'];
    this.userInfoBg = imgs[Math.floor(Math.random() * imgs.length)];

    // Force change detection multiple times to ensure proper rendering
    this.cdr.detectChanges();

    // Additional delayed detection to ensure UI updates
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 10);
  }

  /**
   * Handles detail page navigation dynamically based on current URL and sub-item
   * 
   * This method extracts patterns from the actual navigation items to make the system
   * truly data-driven. It looks for items that have detail-like patterns in their subs.
   * 
   * @param subItem - The sub-item being clicked
   * @returns Navigation array or null if not a detail page
   */
  private handleDetailPageNavigation(subItem: NavigationSubItem): string[] | null {
    const currentUrl = this.router.url;
    
    // Handle specific detail page types that we know about
    const detailPagePatterns = [
      { pattern: /\/task\/task-details\/([^\/]+)(?:\/(.+))?/, basePath: 'task/task-details' },
      { pattern: /\/revision\/revision-details\/([^\/]+)(?:\/(.+))?/, basePath: 'revision/revision-details' },
      { pattern: /\/issue\/issue-details\/([^\/]+)(?:\/(.+))?/, basePath: 'issue/issue-details' },
      { pattern: /\/request\/request-details\/([^\/]+)(?:\/(.+))?/, basePath: 'request/request-details' }
    ];

    for (const detailPage of detailPagePatterns) {
      const match = currentUrl.match(detailPage.pattern);
      if (match) {
        const itemId = match[1];
        const subRoutePath = subItem.to.split('/').pop();

        // Build the base path
        const basePath = `/${detailPage.basePath}/${itemId}`;

        // Handle special cases like 'edit'
        if (subRoutePath === 'edit') {
          return [basePath, 'edit'];
        } else if (subRoutePath) {
          return [basePath, subRoutePath];
        } else {
          // For detail page main route
          return [basePath];
        }
      }
    }

    // Fallback to the old dynamic pattern extraction
    const navigationItems = this.navigationItems;
    
    // Look for items that might be detail pages based on their structure
    for (const item of navigationItems) {
      // Skip project details as they're handled separately
      if (item.name === 'ProjectDetail') {
        continue;
      }

      // Check if this item has subs and if any of them suggest a detail page pattern
      if (item.subs && item.subs.length > 0) {
        // Try to extract a pattern from the first sub-item
        const firstSub = item.subs[0];
        const pattern = this.extractDetailPattern(firstSub.to, item.name);
        
        if (pattern) {
          const match = currentUrl.match(pattern);
          if (match) {
            const itemId = match[1];
            const subRoutePath = subItem.to.split('/').pop();

            // Build the base path dynamically
            const basePath = this.buildDetailPath(pattern, itemId);

            // Handle special cases like 'edit'
            if (subRoutePath === 'edit') {
              return [basePath, 'edit'];
            } else if (subRoutePath) {
              return [basePath, subRoutePath];
            } else {
              // For detail page main route
              return [basePath];
            }
          }
        }
      }
    }

    return null;
  }

  /**
   * Extracts a detail pattern from a sub-item's URL
   * @param subUrl - The URL of a sub-item (e.g., "/task/updates")
   * @param itemName - The name of the parent item
   * @returns Regex pattern for matching detail URLs, or null if not a detail page
   */
  private extractDetailPattern(subUrl: string, itemName: string): RegExp | null {
    // Extract the base path from the sub URL
    // e.g., "/task/updates" -> "/task"
    const urlParts = subUrl.split('/').filter(part => part);
    if (urlParts.length < 2) return null;

    const basePath = `/${urlParts[0]}`;
    const entityName = urlParts[0];
    
    // Check if this looks like a detail page based on item name
    if (itemName.toLowerCase().includes('detail')) {
      // Create pattern for detail pages: /entity/entity-details/id/subroute
      return new RegExp(`\\/${entityName}\\/${entityName}-details\\/([^\\/]+)(?:\\/(.+))?`);
    }

    return null;
  }

  /**
   * Builds the main detail path
   * @param pattern - The regex pattern used to match the URL
   * @param itemId - The ID of the detail item
   * @returns The constructed detail path
   */
  private buildDetailPath(pattern: RegExp, itemId: string): string {
    // Extract the base path from the pattern
    const patternStr = pattern.source;
    const basePathMatch = patternStr.match(/\\\/([^\\\/]+)\\\/[^\\\/]+-details/);
    if (basePathMatch) {
      const entityName = basePathMatch[1];
      return `/${entityName}/${entityName}-details/${itemId}`;
    }
    return '';
  }

  /**
   * Checks if we're currently on a detail page and returns the detail page info
   * @returns Object with detail page info or null if not on a detail page
   */
  private getCurrentDetailPageInfo(): { type: string; id: string; subRoute?: string } | null {
    const currentUrl = this.router.url;
    
    const detailPagePatterns = [
      { type: 'task', pattern: /\/task\/task-details\/([^\/]+)(?:\/(.+))?/ },
      { type: 'revision', pattern: /\/revision\/revision-details\/([^\/]+)(?:\/(.+))?/ },
      { type: 'issue', pattern: /\/issue\/issue-details\/([^\/]+)(?:\/(.+))?/ },
      { type: 'request', pattern: /\/request\/request-details\/([^\/]+)(?:\/(.+))?/ }
    ];

    for (const detailPage of detailPagePatterns) {
      const match = currentUrl.match(detailPage.pattern);
      if (match) {
        return {
          type: detailPage.type,
          id: match[1],
          subRoute: match[2]
        };
      }
    }

    return null;
  }

  /**
   * Handles detail page active state checking dynamically
   * @param subItem - The sub-item to check
   * @param currentUrl - The current URL
   * @returns Boolean indicating if the sub-item is active, or null if not a detail page
   */
  private handleDetailPageActiveCheck(subItem: NavigationSubItem, currentUrl: string): boolean | null {
    // Handle specific detail page types that we know about
    const detailPagePatterns = [
      { pattern: /\/task\/task-details\/([^\/]+)(?:\/(.+))?/, basePath: 'task/task-details' },
      { pattern: /\/revision\/revision-details\/([^\/]+)(?:\/(.+))?/, basePath: 'revision/revision-details' },
      { pattern: /\/issue\/issue-details\/([^\/]+)(?:\/(.+))?/, basePath: 'issue/issue-details' },
      { pattern: /\/request\/request-details\/([^\/]+)(?:\/(.+))?/, basePath: 'request/request-details' }
    ];

    for (const detailPage of detailPagePatterns) {
      const match = currentUrl.match(detailPage.pattern);
      if (match) {
        const subRoutePath = subItem.to.split('/').pop();
        const currentSubRoute = currentUrl.split('/').pop();

        // Handle special cases like 'edit'
        if (subRoutePath === 'edit' && currentSubRoute === 'edit') {
          return true;
        }

        // Handle detail page main route
        if (subRoutePath === undefined && !currentSubRoute) {
          return true;
        }

        return subRoutePath === currentSubRoute;
      }
    }

    // Fallback to the old dynamic pattern extraction
    const navigationItems = this.navigationItems;
    
    // Look for items that might be detail pages based on their structure
    for (const item of navigationItems) {
      // Skip project details as they're handled separately
      if (item.name === 'ProjectDetail') {
        continue;
      }

      // Check if this item has subs and if any of them suggest a detail page pattern
      if (item.subs && item.subs.length > 0) {
        // Try to extract a pattern from the first sub-item
        const firstSub = item.subs[0];
        const pattern = this.extractDetailPattern(firstSub.to, item.name);
        
        if (pattern) {
          const match = currentUrl.match(pattern);
          if (match) {
            const subRoutePath = subItem.to.split('/').pop();
            const currentSubRoute = currentUrl.split('/').pop();

            // Handle special cases like 'edit'
            if (subRoutePath === 'edit' && currentSubRoute === 'edit') {
              return true;
            }

            // Handle detail page main route
            if (subRoutePath === undefined && !currentSubRoute) {
              return true;
            }

            return subRoutePath === currentSubRoute;
          }
        }
      }
    }

    return null;
  }

  getWorkingCount() {
    this.navigationService.getWorkingCount().subscribe({
      next: (response) => {
        // Ensure response.data is a valid array
        const dataArr = Array.isArray(response?.data) ? response.data : [];
        this.statusItems = this.statusItems.map(item => {
          if (item.label === 'Working on') {
            const workingItem = dataArr.find((d: any) => d._id === 'working');
            return { ...item, count: workingItem?.count || 0 };
          } else if (item.label === 'Pending') {
            const requestedItem = dataArr.find((d: any) => d._id === 'requested');
            return { ...item, count: requestedItem?.count || 0 };
          } else if (item.label === 'Approvals') {
            const requestedItem = dataArr.find((d: any) => d._id === 'approval-request');
            return { ...item, count: requestedItem?.count || 0 };
          }
          return item;
        });
      },
      error: (error) => {
        // Silently set all counts to 0 on error
        this.statusItems = this.statusItems.map(item => ({ ...item, count: 0 }));
      }
    });
  }

}
